import { ThemeName } from "@dto/usersResponse.dto";
import { ConfigProvider, theme, ThemeConfig } from 'antd';
import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import logoWhite from "src/assets/logo-white.svg";
import { applyXmCssVars, THEMES, ThemeVars } from 'src/styles/variables';
import { ThemeUtil } from 'src/util/ThemeUtil';
import logoColor from "/xormon-color.svg";

interface ThemeContextValue {
  // isDark: boolean;
  // setDark: (dark: boolean) => void;
  theme: ThemeVars;
  logoSrc: string;
}

const ThemeContext = createContext<ThemeContextValue>({ theme: THEMES.light, logoSrc: logoWhite });

export const useTheme = () => useContext(ThemeContext);

function getThemeKey(theme: ThemeVars): string {
  return 'xm-' + theme?.name;
}

export const ThemeProvider: FC<PropsWithChildren> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => THEMES[ThemeUtil.getThemeName()] ?? THEMES.blue);

  const logoSrc = useMemo(() => currentTheme.name === "light" ? logoColor : logoWhite, [currentTheme]);

  const context = useMemo<ThemeContextValue>(() => ({ theme: currentTheme, logoSrc: logoSrc }), [currentTheme]);

  const setTheme = useCallback((name: ThemeName) => {
    (Object.keys(THEMES) as ThemeName[]).forEach(t => document.body.classList.remove('xm-' + t));
    const th = THEMES[name] ?? THEMES["light"];
    setCurrentTheme(th);
    ThemeUtil.persistThemeMode(name);
    applyXmCssVars(th);
    document.documentElement.style.colorScheme = name === 'dark' ? 'dark' : 'normal';
    document.body.classList.add('xm-' + name);
  }, []);

  useEffect(() => {
    setTheme(ThemeUtil.getThemeName());
    ThemeUtil.onThemeChange = setTheme;
  }, []);

  const themeConfig = useMemo<ThemeConfig>(() => ({
    algorithm: currentTheme.name === 'dark'
      ? [
        theme.darkAlgorithm,
        //theme.compactAlgorithm
      ]
      : null,// theme.compactAlgorithm,
    cssVar: { key: getThemeKey(currentTheme) },
    token: currentTheme.token,
    components: {
      Button: {
        onlyIconSize: currentTheme.token.fontSize,
      },
      Collapse: {
        contentPadding: 8
      },
      DatePicker: {
        borderRadiusSM: 0
      },
      Menu: {
        itemHeight: 25,
        dropdownWidth: 140,
      },
      Notification: {
        // for soft shadow
        marginXXL: 48,
        marginLG: 24,
      },
      Table: {
        cellPaddingBlock: 4,
        cellPaddingBlockSM: 1
      },
      Tree: {
        // titleHeight: currentTheme.token.fontSize // doesn't propagate
      }
    }
  }), [currentTheme]);

  return (
    <ThemeContext.Provider value={context}>
      <ConfigProvider theme={themeConfig}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};
